import moment from "moment-timezone";
import {
  CashDrawerSession,
  CashTransaction,
  DENOMINATION_ORDER,
  DENOMINATION_VALUES,
  DenominationEntry,
  CashDenominationKey,
} from "../types/cashDrawer";

/**
 * Format a UTC ISO timestamp from the backend as a date+time string in the
 * salon's configured timezone. Returns "—" for null / invalid input.
 */
export const formatDrawerDateTime = (
  iso: string | null | undefined,
  timezone: string,
): string => {
  if (!iso) return "—";
  const m = moment.utc(iso).tz(timezone);
  return m.isValid() ? m.format("MMM D, YYYY h:mm A") : "—";
};

/**
 * Module-level formatter so every call reuses one Intl instance.
 * en-US USD gives "$1,234.50" — thousands separators included.
 */
const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

/**
 * Canonical money formatter for the app. Renders null/undefined/NaN as "—",
 * negatives as "-$1,234.50", and (with `showSign`) positives as "+$1,234.50".
 * With `contra`, the amount is treated as a positive magnitude representing
 * money OUT (a refund/discount/contra figure) and rendered in accounting
 * parentheses — "($1,234.50)" — so callers pass the positive value directly,
 * no need to negate. Zero stays plain ("$0.00", not "($0.00)"). `contra` takes
 * precedence over `showSign`. Always format money through this — never inline
 * `$${x.toFixed(2)}`.
 */
export const formatCurrency = (
  amount: number | null | undefined,
  {
    showSign = false,
    contra = false,
  }: { showSign?: boolean; contra?: boolean } = {},
): string => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return "—";
  }
  const formatted = USD_FORMATTER.format(Math.abs(amount));
  if (contra) return amount === 0 ? formatted : `(${formatted})`;
  if (amount < 0) return `-${formatted}`;
  if (showSign && amount > 0) return `+${formatted}`;
  return formatted;
};

export const denominationsToTotal = (entries: DenominationEntry[]): number => {
  return entries.reduce(
    (acc, e) =>
      acc + (DENOMINATION_VALUES[e.denomination] ?? 0) * (e.count || 0),
    0,
  );
};

export const emptyDenominations = (): DenominationEntry[] =>
  DENOMINATION_ORDER.map((denomination) => ({ denomination, count: 0 }));

/**
 * Given a target amount, return a greedy bill breakdown useful for pre-filling
 * change-given forms. Not guaranteed to match the physical drawer.
 */
export const suggestDenominationsForAmount = (
  amount: number,
): DenominationEntry[] => {
  const entries: DenominationEntry[] = [];
  let remainder = Math.round(amount * 100);
  for (const denom of DENOMINATION_ORDER) {
    const unitCents = Math.round(DENOMINATION_VALUES[denom] * 100);
    const count = Math.floor(remainder / unitCents);
    entries.push({ denomination: denom as CashDenominationKey, count });
    remainder -= count * unitCents;
  }
  return entries;
};

export const computeExpectedBalance = (session: CashDrawerSession): number => {
  const transactions = session.transactions ?? [];
  const opening = Number(session.opening_balance) || 0;
  const net = transactions.reduce((acc, t) => {
    if (t.voided_at || t.type === "opening_balance") return acc;
    return acc + Number(t.direction) * Number(t.amount);
  }, 0);
  return Math.round((opening + net) * 100) / 100;
};

export const computeVariance = (counted: number, expected: number): number =>
  Math.round((counted - expected) * 100) / 100;

export const formatTransactionType = (
  tx: Pick<CashTransaction, "type" | "custom_type_label" | "payout_category">,
): string => {
  // Prefer the staff-supplied label so manual adjustments read naturally.
  if (tx.custom_type_label) return tx.custom_type_label;
  if (tx.type === "cash_out" && tx.payout_category) {
    return tx.payout_category === "expense" ? "Expense" : "Deposit";
  }
  switch (tx.type) {
    case "sale":
      return "Cash sale";
    case "refund":
      return "Refund";
    case "cash_in":
      return "Pay in";
    case "cash_out":
      return "Pay out";
    case "opening_balance":
      return "Opening balance";
    case "closeout":
      return "Count drawer";
    case "other":
      return "Other";
    default:
      return tx.type;
  }
};

const formatSessionWindow = (session: CashDrawerSession): string => {
  const parts: string[] = [];
  if (session.opened_at) parts.push(`Opened ${session.opened_at}`);
  if (session.closed_at) parts.push(`Closed ${session.closed_at}`);
  return parts.join(" / ");
};

export const toCsv = (session: CashDrawerSession): string => {
  const m = session.metrics;
  const rows: string[][] = [
    ["Session window", formatSessionWindow(session) || "—"],
    ["Status", session.status],
    ["Opening balance", String(session.opening_balance)],
    ["Expected balance", String(session.expected_balance)],
    ["Counted balance", String(session.counted_balance ?? "")],
    ["Variance", String(session.variance ?? "")],
    [],
    ["Cash sales", m ? String(m.cash_sales) : ""],
    ["Refunds", m ? String(m.refunds) : ""],
    ["Paid in", m ? String(m.paid_in) : ""],
    ["Expenses", m ? String(m.expenses) : ""],
    ["Deposits", m ? String(m.deposits) : ""],
    ["Tips", m ? String(m.tips) : ""],
    [],
    ["Time", "Type", "Description", "Staff", "Amount", "Running balance"],
  ];
  let running = Number(session.opening_balance) || 0;
  (session.transactions ?? []).forEach((tx) => {
    const signed = Number(tx.direction) * Number(tx.amount);
    if (!tx.voided_at && tx.type !== "opening_balance") {
      running += signed;
    }
    rows.push([
      tx.created_dt ?? "",
      formatTransactionType(tx),
      tx.description ?? "",
      tx.staff_id ?? "",
      signed.toFixed(2),
      running.toFixed(2),
    ]);
  });
  return rows
    .map((row) =>
      row
        .map((cell) => {
          const s = String(cell ?? "");
          return s.includes(",") || s.includes('"') || s.includes("\n")
            ? `"${s.replace(/"/g, '""')}"`
            : s;
        })
        .join(","),
    )
    .join("\n");
};

export const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const toIsoDate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Today's calendar date in the salon's timezone, as a YYYY-MM-DD string.
 * Drives the default for the Balancing Date picker so a salon closing out
 * just past midnight UTC still sees the right local date.
 */
export const todayInSalonTimezone = (timezone: string): string => {
  const m = moment.tz(timezone);
  return m.isValid() ? m.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
};

/**
 * Format a YYYY-MM-DD calendar date for display. Avoids ``new Date(str)``
 * (which UTC-parses and shifts in negative offsets); reads the components
 * directly and constructs a local Date so the visible date doesn't shift.
 */
export const formatBalancingDate = (
  value: string | null | undefined,
): string => {
  if (!value) return "—";
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  const [y, mo, d] = parts.map((p) => Number(p));
  if (!y || !mo || !d) return value;
  const local = new Date(y, mo - 1, d);
  return moment(local).format("MMM D, YYYY");
};
